import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Segment, Icon, Button, Header } from 'semantic-ui-react'
import Menu from './MenuComponent'
import SliderItem from './SliderItemComponent'
import { getStudy, sendMail } from './FormsApi'
import { ahpFunction } from './ahpComponent'
import { fullMarks, revertResult } from '../addons'
import { useHistory } from "react-router-dom";
import moment from 'moment'
import translations from '../../translations'
import { applyStudyTranslation, createNamesLists } from '../../utils/applyStudyTranslation'
import { sanitizeHtml, isEmptyHtmlContent } from '../../utils/sanitizeHtml'

const t = translations

const renderStudyPageHtml = (html, fallbackHtml) => {
    const contentSource = isEmptyHtmlContent(html) ? fallbackHtml : html
    if (isEmptyHtmlContent(contentSource)) {
        return null
    }
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(contentSource)
    const content = looksLikeHtml ? contentSource : String(contentSource).replace(/\n/g, '<br>')
    return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
}

const FormsComponent = ({ navigation }) => {
    const DEFAULT_BREAK_TIME = 5 * 60 * 1000
    const [study, setStudy] = useState(null)
    const [names, setNames] = useState(null)
    const [site, setSite] = useState(0)
    const [checkBySite, setCheckBySite] = useState({})
    const [time, setTime] = useState(DEFAULT_BREAK_TIME)
    const [pause, setPause] = useState(true)
    const timerRef = useRef(null)
    const history = useHistory()

    const startTime = Math.max(0, study?.breakDurationMs ?? DEFAULT_BREAK_TIME)

    const updateMarkResult = (markId, resultIndex, value) => {
        setStudy((previousStudy) => {
            if (!previousStudy) {
                return previousStudy
            }

            const normalizedMarkId = String(markId)
            return {
                ...previousStudy,
                marks: (previousStudy.marks || []).map((mark) => {
                    if (String(mark._id) !== normalizedMarkId) {
                        return mark
                    }

                    const results = [...(mark.results || [])]
                    results[resultIndex] = value
                    return { ...mark, results }
                })
            }
        })
    }

    useEffect(() => {
        const fetchData = async () => {
            const newStudy = await getStudy(localStorage.getItem('ahpUser'))
            if (newStudy?.study) {
                const userLanguage = localStorage.getItem('ahpLanguage') || 'pl'
                const translatedStudy = applyStudyTranslation(newStudy.study, userLanguage)
                setStudy(translatedStudy)
                setNames(createNamesLists(translatedStudy))
            }
        }
        fetchData()
    }, [])

    const steps = useMemo(() => {
        if (!study?.pages || study.pages.length === 0) {
            return [{ type: 'intro' }, { type: 'end' }]
        }
        return study.pages.map((page) => ({
            type: page.type,
            title: page.title,
            text: page.text,
            markLocalKeys: page.markLocalKeys || []
        }))
    }, [study])

    const currentStep = steps[site] || { type: 'intro' }
    const marksByLocalKey = useMemo(() => {
        const map = {}
        ;(study?.marks || []).forEach((mark, markIndex) => {
            const key = mark.localKey || `legacy_${markIndex}`
            map[key] = { ...mark, localKey: key, markIndex }
        })
        return map
    }, [study])

    const currentMarks = (currentStep.type === 'categories' ? (currentStep.markLocalKeys || []) : [])
        .map((key) => marksByLocalKey[key])
        .filter(Boolean)
    const checked = Boolean(checkBySite[site])

    useEffect(() => {
        setTime(startTime)
    }, [startTime])

    useEffect(() => {
        if (currentStep.type === 'break') {
            setPause(true)
            setTime(startTime)

            if (startTime <= 0) {
                setPause(false)
                return undefined
            }

            if (timerRef.current) {
                clearInterval(timerRef.current)
            }

            timerRef.current = setInterval(() => {
                setTime((previousTime) => {
                    if (previousTime <= 1000) {
                        clearInterval(timerRef.current)
                        timerRef.current = null
                        setPause(false)
                        return 0
                    }
                    return previousTime - 1000
                })
            }, 1000)
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            setPause(false)
            setTime(startTime)
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [currentStep.type, startTime])

    const getResultNumber = (x, y, length) => {
        let pom = 0
        let count = 0
        if (y > x) {
            while (count < x) {
                count += 1
                pom += length - count
            }
            pom += y - x - 1
        } else {
            while (count < y) {
                count += 1
                pom += length - count
            }
            pom += x - y - 1
        }
        return pom
    }

    const renderTableNumber = (x, y, length, data) => {
        if (x === y) return 1
        let pom = 0
        let count = 0
        if (y > x) {
            while (count < x) {
                count += 1
                pom += length - count
            }
            pom += y - x - 1
            return fullMarks[data[pom]]
        }
        while (count < y) {
            count += 1
            pom += length - count
        }
        pom += x - y - 1
        return revertResult[fullMarks[data[pom]]]
    }

    const markDiagnostics = useMemo(() => {
        const diagnostics = {}
        currentMarks.forEach((mark) => {
            const markIndex = mark.markIndex
            if (!mark?.namesList?.length || !mark?.results) {
                diagnostics[markIndex] = { cr: null, maxProblem: null }
                return
            }

            const ahpResult = ahpFunction(mark.namesList, mark.results, true)
            const currentCr = ahpResult?.itemRankMetaMap?.tree?.cr
            const rank = []
            Object.keys(ahpResult?.rankedScoreMap || {}).forEach((key) => {
                rank.push(ahpResult.rankedScoreMap[key])
            })

            if (!rank.length) {
                diagnostics[markIndex] = { cr: currentCr !== undefined ? currentCr.toFixed(3) : null, maxProblem: null }
                return
            }

            const saaty2 = []
            for (let i = 0; i < rank.length; i += 1) {
                saaty2.push([])
                for (let j = 0; j < rank.length; j += 1) {
                    saaty2[i].push((eval(renderTableNumber(i, j, mark.namesList.length, mark.results)) * rank[j] / rank[i]).toFixed(3))
                }
            }

            let max = 0
            let xx
            let xy
            for (let i = 0; i < rank.length; i += 1) {
                for (let j = i + 1; j < rank.length; j += 1) {
                    if (saaty2[i][j] < saaty2[j][i]) {
                        if (parseFloat(saaty2[j][i]) > max) {
                            max = parseFloat(saaty2[j][i])
                            xx = j
                            xy = i
                        }
                    } else if (max < parseFloat(saaty2[i][j])) {
                        max = parseFloat(saaty2[i][j])
                        xx = i
                        xy = j
                    }
                }
            }

            diagnostics[markIndex] = {
                cr: currentCr !== undefined ? currentCr.toFixed(3) : null,
                maxProblem: getResultNumber(xx, xy, mark.namesList.length)
            }
        })
        return diagnostics
    }, [currentMarks])

    const getTimer = () => {
        let timeString = ''
        moment.duration(time).minutes() < 10 && (timeString += '0')
        timeString += moment.duration(time).minutes()
        timeString += ' : '
        moment.duration(time).seconds() < 10 && (timeString += '0')
        timeString += moment.duration(time).seconds()
        return timeString
    }

    const startPage = () => (
        <Segment style={{ margin: 'auto', padding: 50, width: 600 }}>
            {renderStudyPageHtml(currentStep?.text, t.forms.startPage)}
        </Segment>
    )

    const middlePage = () => (
        <Segment style={{ margin: 'auto', padding: 50, width: 600 }}>
            {renderStudyPageHtml(currentStep?.text, t.forms.middlePage)}
            <div>{getTimer()}</div>
        </Segment>
    )

    const endPage = () => (
        <Segment style={{ margin: 'auto', padding: 50, width: 600, display: 'flex', flexDirection: 'column' }}>
            {renderStudyPageHtml(currentStep?.text, t.forms.endPage)}
            <Button style={{ marginTop: 50 }} onClick={() => {
                history.push('/end')
                sendMail()
            }}>{t.buttons.send}</Button>
        </Segment>
    )

    const isComparisonStep = currentStep.type === 'categories'

    return (
        <div style={{ backgroundColor: '#fcf5f5', minHeight: '100vh' }}>
            <Menu navigation={navigation} />
            {currentStep.type === 'start' || currentStep.type === 'intro' ? startPage() :
                currentStep.type === 'break' ? middlePage() :
                    currentStep.type === 'end' ? endPage() :
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Segment style={{ width: 1000, margin: '0 20px', height: '100%' }}>
                                {currentStep?.title ? <Header as='h2' style={{ marginBottom: 25 }}>{currentStep.title}</Header> : null}
                                {currentMarks.map((mark) => {
                                    const markIndex = mark.markIndex
                                    const markHeader = mark?.title || `${t.forms.mark} ${markIndex + 1}`
                                    const markCr = markDiagnostics?.[markIndex]?.cr
                                    const markMaxProblem = markDiagnostics?.[markIndex]?.maxProblem
                                    return (
                                        <div key={mark?._id || markIndex} style={{ marginBottom: 35 }}>
                                            <Header as='h3' style={{ marginBottom: 30 }}>{markHeader}</Header>
                                            {study && names && mark && mark.results.map((result, j) =>
                                                <div style={{
                                                    marginBottom: 40,
                                                    border: checked && markMaxProblem === j && markCr && markCr > 0.1 ? '4px solid red' : 'none',
                                                    padding: checked && markMaxProblem === j && markCr && markCr > 0.1 ? 15 : 0,
                                                    borderRadius: 10
                                                }} key={`${markIndex}-${j}`}>
                                                    <SliderItem
                                                        names={names?.[markIndex]?.[j] || ''}
                                                        value={result}
                                                        mark={mark._id}
                                                        number={j}
                                                        onResultChange={updateMarkResult}
                                                        studyId={study._id}
                                                    />
                                                    {checked && markMaxProblem === j && markCr && markCr > 0.1 &&
                                                        <div style={{ color: 'red', marginTop: 40 }}><Icon color='red' name='warning sign' />
                                                            <b>{t.forms.validation.maxInconsistency} {result > 40 ? '<--' : '-->'}</b>
                                                        </div>}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </Segment>
                        </div>
            }
            {isComparisonStep && <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                <Button onClick={() => {
                    setCheckBySite(prev => ({ ...prev, [site]: true }))
                }}>{t.buttons.check}</Button>
            </div>}
            {checked && isComparisonStep && currentMarks.map((mark) => {
                const markIndex = mark.markIndex
                const markHeader = mark?.title || `${t.forms.mark} ${markIndex + 1}`
                const markCr = markDiagnostics?.[markIndex]?.cr
                return (
                    <div key={`summary-${markIndex}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20 }}>
                        <div>{markHeader}:</div>
                        {markCr && markCr > 0.1
                            ? <b style={{ color: 'red' }}>{t.forms.validation.answersInconsistent}</b> : <b style={{ color: 'green' }}>{t.forms.validation.answersConsistent}</b>}
                    </div>
                )
            })}
            <div style={{ display: 'flex', justifyContent: 'center', margin: 20 }}>
                {site > 0 && <Button onClick={() => { setSite(site - 1) }}>{t.buttons.back}</Button>}
                {site < steps.length - 1 && <Button onClick={() => { setSite(site + 1) }} disabled={pause && currentStep.type === 'break'}>{t.buttons.next}</Button>}
            </div>
        </div>)
}

export default FormsComponent